import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { DisplayMonetaryValue } from './allInTotal';
import { hasOnsiteCharges, hasOnsiteChargesValue, pickDisplayTotal, pickDisplayTotalString } from './allInTotal';

// Re-exported so components can take the total and the arm from one import. Non-React modules must
// import them from './allInTotal' instead — this file reaches the feature-flag provider's graph.
export { hasOnsiteCharges, hasOnsiteChargesValue, pickDisplayTotal, pickDisplayTotalString };
export type { DisplayMonetaryValue };

/**
 * Whether this viewer sees the all-in whole-stay total — taxes plus the fees the guest pays at the
 * property — instead of the total Joy collects (TRAV-4298).
 *
 * Every surface that renders a total AND every list that orders by one must read the arm from here.
 * If a list rendered `allInTotalRate` while ordering by `totalRate`, a low-to-high sort would come
 * out visibly out of order, which is the failure this single entry point exists to prevent.
 */
export const useIsAllInTotalEnabled = (): boolean => useAllInTotalArm().isEnabled;

/**
 * The arm plus whether it has settled yet.
 *
 * While the remote flag loads, `useIsFeatureEnabled` reports the default value — `control` — so
 * `isEnabled` reads false for a treatment viewer and then flips. That is harmless for anything
 * recomputed from props on every render, which is why `useIsAllInTotalEnabled` hides it. It is not
 * harmless for state committed once off the arm: read `isLoading` there and wait, or the state
 * keeps a decision made under the wrong arm. `useFilteredRates` is the case in point — its default
 * rate selection is sticky, and a re-sort never evicts an already-selected token.
 */
export const useAllInTotalArm = (): Readonly<{ isEnabled: boolean; isLoading: boolean }> => {
  const [isEnabled, isLoading] = useIsFeatureEnabled('travelAllInTotalExperiment');
  return { isEnabled, isLoading };
};

/**
 * The whole-stay total to render, plus whether it actually folded in a property-paid charge.
 *
 * `includesPropertyCharges` gates copy only. It is false whenever the charge could not be
 * determined, so a card never claims property charges are covered by a figure that does not
 * contain them — the all-in total is a best-available figure, not a guaranteed ceiling.
 */
export const useDisplayTotal = <T extends DisplayMonetaryValue>(
  collectedTotal: T,
  allInTotal: Maybe<T> | undefined,
  onsiteChargesTotal: Maybe<DisplayMonetaryValue> | undefined,
): Readonly<{ total: T; includesPropertyCharges: boolean }> => {
  const isAllInEnabled = useIsAllInTotalEnabled();
  return {
    total: pickDisplayTotal(collectedTotal, allInTotal, isAllInEnabled),
    includesPropertyCharges: isAllInEnabled && hasOnsiteCharges(onsiteChargesTotal),
  };
};
